import * as React from 'react'
import { useCallback, useEffect, useState } from 'react'
import { useGoogleReCaptcha, GoogleReCaptchaProvider } from 'react-google-recaptcha-v3'

import { UseEmailAPI, UseVerifyReCaptchaAPI, useContentAPI } from '../../../Hooks/useAPI'
import { TrackGAEvent } from '../../GoogleAnalytics'
import PrimaryButton from '../../atoms/Button/Primary/Button'
import DropdownComp from '../../atoms/Form/Dropdown'
import InputComp from '../../atoms/Form/Input'
import TextAreaWithClose from '../../atoms/Form/Textarea'
import Paragraph from '../../atoms/Paragraph'
import Modal from '../../molecules/Modal'
import SuccessBlock from '../../molecules/SuccessBlock'
import { FormValidations } from './validations'

const enableReCaptcha = process.env.REACT_APP_ENABLE_RECAPTCHA

/**
 * Contact Us Enquiry Form
 *
 */

const ContactUs = () => {
  const { data } = useContentAPI('contentModel', 'login', '', false)
  const { executeRecaptcha } = useGoogleReCaptcha()
  const { validations } = FormValidations()
  const [invalidFocus, setInvalidFocus] = useState(false)
  const [validationData, setValidationData] = useState(validations)
  const [currentForm, setCurrentForm] = useState<any>(null)
  const [success, setSuccess] = useState(false)
  const [msg, setMsg] = useState('')
  const [start, setStart] = useState(false)
  const [submitting, setSubmitting] = useState(false);


  const verifyReCaptcha = useCallback(async () => {
    if (!executeRecaptcha) {
      console.log('Recaptcha not available')
      return
    }
    const token = await executeRecaptcha('enquiryForm')
    return token
  }, [executeRecaptcha])

  const debounce = (callback, wait) => {
    let timeoutId = null
    return (...args) => {
      window.clearTimeout(timeoutId)
      timeoutId = window.setTimeout(() => {
        callback(...args)
      }, wait)
    }
  }

  const validateFields = (name: string, value: string) => {
    const dataField = data?.data?.fields?.filter((item) => item?.id === name)
    let field = validationData[name]

    validationData[name] = {
      ...validationData[name],
      error: false,
      errorMsg: '',
    }

    if ((dataField[0]?.required || field?.required) && value === '') {
      validationData[name] = {
        ...validationData[name],
        error: true,
        errorMsg: validationData[name]?.requiredMsg,
      }
    }
    if (value !== '') {
      const regex = field.pattern ? field.pattern : ''
      if (regex && regex !== '') {
        const result = regex.test(value)
        if (!result) {
          validationData[name] = {
            ...validationData[name],
            error: true,
            errorMsg: validationData[name]?.validationMsg,
          }
        }
      }
    }
    setValidationData({
      ...validationData,
    })
  }

  const submitForm = async (formData: any, form: any) => {
     if (submitting) return;           // guard to prevent multiple calls
    setSubmitting(true);
    let values = {}
    values['form'] = 'contact-enquiry'

    for (let d in formData) {
      const filteredValue = data?.data?.fields?.filter((item) => item.id === d)
      values[filteredValue[0]?.name] = formData[d]
    }

    if (enableReCaptcha === '0') {
      const response = await UseEmailAPI(values, 'contactUs')

      if (response.status === 200) {
        setSuccess(true)
        setMsg(validationData['success'])
        TrackGAEvent('form_submit', 'Contact Enquiry', window.location.pathname)
        form.reset()
      } else {
        setSuccess(false)
        setMsg(validationData['error'])
      }
      setSubmitting(false);
    } else {
      const reCaptchaToken = await verifyReCaptcha()
      const verifyRecaptcha = await UseVerifyReCaptchaAPI({ token: reCaptchaToken })

      if (verifyRecaptcha?.data?.success) {
        const response = await UseEmailAPI(values, 'contactUs')

        if (response.status === 200) {
          setSuccess(true)
          setMsg(validationData['success'])
          TrackGAEvent('form_submit', 'Contact Enquiry', window.location.pathname)
          form.reset()
        } else {
          setSuccess(false)
          setMsg(validationData['error'])
        }
      } else {
        setSuccess(false)
        setMsg(verifyRecaptcha?.data?.msg)
      }
       setSubmitting(false);
    }
  }

  const handleSubmit = (e) => {
    e.stopPropagation()
    e.preventDefault()
    setSuccess(false)
    let obj = {}
    const form = e.target.closest('.customForms')
    setCurrentForm(form)
    const formDetails: any = new FormData(form)
    for (const [key, value] of formDetails) {
      obj[key] = value
      validateFields(key, value)
    }
    setInvalidFocus(true)
    const success = Object.values(validationData).filter((item) => item.error)
    if (success.length === 0) {
      submitForm(obj, form)
    }
  }

  const onFieldsChange = debounce((name, value) => {
    if (!start) {
      TrackGAEvent('form_start', 'Contact Enquiry', window.location.pathname)
      setStart(true)
    }
    validateFields(name, value)
  }, 1000)

  useEffect(() => {
    if (invalidFocus && currentForm !== null) {
      const errorElement: any = currentForm.querySelector('.invalid')
      errorElement !== null && errorElement.focus()
      setInvalidFocus(false)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [validationData, invalidFocus])

  return (
    <>
      <div className="layoutSpacing formsDiv">
        {!success && (
          <div data-testid="contactform" id="contactform" className="layout essForms sectionBottomSpacing hidden w-100">
            <div className="text-right">
              <Paragraph content="* Mandatory fields" classes="!text-base" />
            </div>
            <form className="customForms">
              {data?.data?.fields?.map((field, index) => (
                <div key={`${field.id}-${index}`}>
                  {field?.id !== 'componentName' && (
                    <>
                      {field?.type === 'Array' && (
                        <DropdownComp
                          label={`${field?.name} ${validationData[field?.id]?.required || validationData[field?.id]?.required ? '*' : ''}`}
                          name={field?.id}
                          error={validationData[field?.id]?.error}
                          errorMessage={validationData[field?.id]?.errorMsg}
                          helpText={field?.helpText}
                          options={field?.items?.validations[0].in}
                          withCheckBox={false}
                          required={validationData[field?.id]?.required}
                          onSelect={(value) => onFieldsChange(field?.id, value)}
                          defaultOption={validationData[field?.id]?.placeholder}
                        />
                      )}
                      {field?.type === 'Symbol' && (
                        <>
                          <InputComp
                            label={`${field?.name} ${validationData[field?.id]?.required || validationData[field?.id]?.required ? '*' : ''}`}
                            name={field?.id}
                            placeholder={validationData[field?.id]?.placeholder}
                            error={validationData[field?.id]?.error}
                            errorMessage={validationData[field?.id]?.errorMsg}
                            helpText={field?.helpText}
                            variant="text"
                            required={validationData[field?.id]?.required}
                            onChange={(name, value) => onFieldsChange(name, value)}
                          />
                        </>
                      )}
                      {field?.type === 'Integer' && (
                        <InputComp
                          label={`${field?.name} ${validationData[field?.id]?.required || validationData[field?.id]?.required ? '*' : ''}`}
                          name={field?.id}
                          placeholder={validationData[field?.id]?.placeholder}
                          error={validationData[field?.id]?.error}
                          errorMessage={validationData[field?.id]?.errorMsg}
                          helpText={validationData[field?.id]?.helpText}
                          variant="text"
                          required={field?.required}
                          onChange={(name, value) => onFieldsChange(name, value)}
                        />
                      )}
                      {field?.type === 'Text' && (
                        <TextAreaWithClose
                          label={`${field?.name} ${validationData[field?.id]?.required || validationData[field?.id]?.required ? '*' : ''}`}
                          name={field?.id}
                          placeholder={validationData[field?.id]?.placeholder}
                          className=""
                          error={validationData[field?.id]?.error}
                          errorMessage={validationData[field?.id]?.errorMsg}
                          helpText={field?.helpText}
                          onChange={(name, value) => onFieldsChange(name, value)}
                          maxChar={250}
                        />
                      )}
                      {field?.type === 'Date' && (
                        <InputComp
                          label={`${field?.name} ${validationData[field?.id]?.required || validationData[field?.id]?.required ? '*' : ''}`}
                          name={field?.id}
                          placeholder={validationData[field?.id]?.placeholder}
                          error={validationData[field?.id]?.error}
                          errorMessage={validationData[field?.id]?.errorMsg}
                          helpText={field?.helpText}
                          variant="date"
                          required={validationData[field?.id]?.required}
                          onChange={(name, value) => onFieldsChange(name, value)}
                          min={validationData[field?.id]?.min || ''}
                          max={validationData[field?.id]?.max || ''}
                        />
                      )}
                    </>
                  )}
                </div>
              ))}
              {!success && (
                <Paragraph
                  content={msg}
                  classes={`${validationData['success'] === msg ? '!text-success-100' : '!text-danger-100'}`}
                />
              )}
              <div className="w-max" onClick={(e) => handleSubmit(e)}>
                <PrimaryButton variant="dark" name="Submit" buttonType="submit" classes="mt-24" disabled={submitting} />
              </div>
            </form>
          </div>
        )}

        {success && (
          <SuccessBlock
            content={{ title: validationData['successTitle'], description: validationData['successMsg'] }}
          />
        )}
        {start && <Modal />}
      </div>
    </>
  )
}

const ContactUsForm = () => {
  return (
    <GoogleReCaptchaProvider reCaptchaKey={process.env.REACT_APP_RECAPTCHA_SITE_KEY}>
      <ContactUs />
    </GoogleReCaptchaProvider>
  )
}

export default ContactUsForm
